import type { ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { cn } from './cn';
import { IconButton } from './Button';

export interface ActionMenuItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Render a divider ABOVE this item. */
  dividerBefore?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Accessible label for the trigger button. */
  label?: string;
  /** Custom trigger; defaults to a kebab IconButton. */
  trigger?: ReactNode;
  align?: 'start' | 'end';
  className?: string;
}

/**
 * Overflow ("kebab") action menu, built on Radix DropdownMenu — portalled,
 * collision-aware, and fully keyboard operable. The audit's biggest usability
 * finding was rows crammed with 5–8 tiny icon buttons; the convention is now
 * 1–2 primary inline actions + the rest collapsed into this menu.
 *
 * The trigger is a VERTICAL kebab: a row of horizontal dots sat on the same axis as the inline
 * icon buttons beside it and read as a sixth button rather than "more".
 */
export function ActionMenu({ items, label = 'More actions', trigger, align = 'end', className }: ActionMenuProps) {
  if (!items.length) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        {trigger ?? (
          <IconButton
            diameter={36}
            aria-label={label}
            className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20"
          >
            <MoreVertical />
          </IconButton>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          className={cn(
            'z-[var(--z-dropdown)] min-w-[184px] overflow-hidden rounded-card border border-line bg-surface p-1 shadow-pop',
            'animate-pop-in',
            className,
          )}
        >
          {items.map((it) => (
            <div key={it.key}>
              {it.dividerBefore && <div className="my-1 h-px bg-line" />}
              <DropdownMenu.Item
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded-control px-2.5 py-2 text-sm outline-none',
                  'data-[highlighted]:bg-surface-2 data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
                  it.danger
                    ? 'text-danger-600 data-[highlighted]:bg-danger-500/10 dark:text-danger-500'
                    : 'text-content',
                  '[&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-current',
                )}
              >
                {it.icon}
                <span className="truncate">{it.label}</span>
              </DropdownMenu.Item>
            </div>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
